import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { IEmployeePermissions } from '../../types/employee';
import { IReportRegistration } from '../../types/report';

import { useUserContext } from '../../contexts/userContext';

import { ErrorProvider } from '../../classes/errorProvider';
import { ReportService } from '../../services/reportService';

import { AppConfig } from '../../config/appConfig';

import '../../styles/reportRegistrationDetailsForm.scss';
import { UtilityService } from '../../services/utilityService';
import { isTemplateExpression } from 'typescript';


export interface IReportRegistrationDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: IReportRegistration;
}

const ReportRegistrationDetailsForm: React.FunctionComponent<IReportRegistrationDetailsForm> = ({ action, id, details }) => {

	const navigate = useNavigate();
	const userContext = useUserContext();
	const [formFields, setFormFields] = useState<IReportRegistration>(details);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [permissionOptions, setPermissionOptions] = useState<string[]>([]);

	useEffect(() => {

		const permissions = Object.keys(userContext.permissions)
			.map((key) => `${key.substring(0, 1).toUpperCase()}${key.substring(1)}`)
			.sort((a, b) => UtilityService.compareStrings(a, b));
		setPermissionOptions(permissions);
	}, []);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		clearSaveResult();
		setIsSaving(true);

		var payload: IReportRegistration = JSON.parse(JSON.stringify(formFields));

		// API is expecing workspace ID to be a GUID or null, not empty string which fails GUID parsing
		payload.workspaceId = payload.workspaceId?.length ? payload.workspaceId : undefined;

		if (action == "add") {
			ReportService.register(payload)
				.then((response) => {
					console.log('ReportService.register: SUCCESS', response);
					setSuccessMessage("Report has been registered.");
					setTimeout(() => {
						navigate("/report/registration");
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('ReportService.register: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
		else if (action == "edit" && id) {
			ReportService.setRegistration(payload)
				.then((response) => {
					console.log('ReportService.setRegistration: SUCCESS', response);
					setSuccessMessage("Report registration details have been saved.");
					setTimeout(() => {
						navigate("/report/registration");
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('ReportService.setRegistration: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<section className="hwi-report-registration-details-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<Form onSubmit={(event) => handleSubmit(event)}>
				<div className="row">
					<InputLabel>Report Name (from Power BI) *</InputLabel>
					<TextField className="name-field" required name="name" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }} inputProps={{ maxLength: 100 }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Display Name *</InputLabel>
					<TextField className="display-name-field" required name="displayName" value={formFields.displayName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["displayName"]: event.target.value }) }} inputProps={{ maxLength: 255 }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Power BI Report ID *</InputLabel>
					<TextField className="report-id-field" required name="reportId" value={formFields.reportId} size="small" onChange={(event) => { setFormFields({ ...formFields, ["reportId"]: event.target.value }) }} inputProps={{ maxLength: 36 }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Power BI Workspace ID</InputLabel>
					<TextField className="workspace-id-field" name="workspaceId" value={formFields.workspaceId} placeholder='System Default' size="small" onChange={(event) => { setFormFields({ ...formFields, ["workspaceId"]: event.target.value }) }} inputProps={{ maxLength: 36 }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Permission</InputLabel>
					<Select
						name="permission"
						className='permission-field'
						value={formFields.permission || '0'}
						fullWidth
						onChange={(event) => { setFormFields({ ...formFields, ["permission"]: event.target.value }) }}
					>
						<MenuItem value="0">(None)</MenuItem>
						{permissionOptions.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
					<FormControlLabel className="active-field" control={<Checkbox name="active" value={formFields.active} checked={formFields.active} size="small" onChange={(event) => { setFormFields({ ...formFields, ["active"]: event.target.checked }) }}></Checkbox>} label="Active" />
				</div>
				<div className="row">
					<FormControlLabel className="listed-field" control={<Checkbox name="listed" value={formFields.ordinal != 0} checked={formFields.ordinal != 0} size="small" onChange={(event) => { setFormFields({ ...formFields, ["ordinal"]: event.target.checked ? 1 : 0 }) }}></Checkbox>} label="Listed" />
				</div>
				<div className="row controls">
					<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save Details"}</Button>
				</div>
			</Form>
			{/* <Json data={formFields}></Json> */}
		</section>
	)
}

export default ReportRegistrationDetailsForm;